import type { HudStore } from "./hud/store.ts";
import { writeHealthNumbers, type SettingsStore } from "./settings.ts";

/**
 * The page's health numbers setting (ADR-061's amendment "the player card", the author's ruling of 2026-09-27: the
 * health number shows by default, and Settings can hide it), as `NameplateSettings` holds the Nameplates choices: made
 * from what the store holds as the page loads, put in the HUD's store, which the player card reads, and taking each
 * change Settings makes, which applies at once and is kept per viewer.
 *
 * @example
 * ```ts
 * const healthNumbers = new HealthNumbersSetting(hud, readHealthNumbers());
 * healthNumbers.setShow(false);
 * ```
 */
export class HealthNumbersSetting {
  /**
   * @param hud The HUD's store, which the player card reads.
   * @param show Whether the numbers show as the page loads, as `readHealthNumbers` gives it.
   * @param store Where a change is kept; this browser's `localStorage` when omitted.
   */
  constructor(
    private readonly hud: Pick<HudStore, "setHealthNumbers" | "getSnapshot">,
    show: boolean,
    private readonly store?: SettingsStore | null,
  ) {
    hud.setHealthNumbers(show);
  }

  /**
   * Whether the health numbers show now.
   *
   * @returns The setting.
   */
  get show(): boolean {
    return this.hud.getSnapshot().healthNumbers;
  }

  /**
   * Takes the viewer's choice, which the player card shows at once, and keeps it.
   *
   * @param show Whether the numbers show.
   */
  setShow(show: boolean): void {
    this.hud.setHealthNumbers(show);
    writeHealthNumbers(show, this.store);
  }
}
